import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  RiDeleteBinLine,
  RiArrowLeftSLine,
  RiArrowRightSLine,
  RiEyeLine,
  RiCloseLine,
  RiMailLine,
  RiPhoneLine,
  RiUserLine,
  RiCalendarLine,
  RiBriefcaseLine,
  RiFileTextLine,
} from "react-icons/ri";
import toast from "react-hot-toast";
import { confirmAndDelete } from "../../utils/confirmAndDelete";

const RequestCardSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200 animate-pulse">
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <div className="h-5 bg-gray-200 rounded w-32"></div>
          <div className="h-4 bg-gray-200 rounded w-24"></div>
        </div>
        <div className="w-8 h-8 bg-gray-200 rounded"></div>
      </div>
      <div className="space-y-2">
        <div className="h-4 bg-gray-200 rounded w-full"></div>
        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
      </div>
      <div className="flex items-center space-x-4">
        <div className="h-4 bg-gray-200 rounded w-24"></div>
        <div className="h-4 bg-gray-200 rounded w-32"></div>
      </div>
    </div>
  </div>
);

const JobApplications = () => {
  const [applications, setApplications] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    currentPage: 1,
    totalPages: 1,
    limit: 6,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const itemsPerPage = 6;
  const totalPages = pagination.totalPages;

  const fetchRequests = async (page = 1) => {
    try {
      setLoading(true);

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/job-application/get-all?page=${page}&limit=${itemsPerPage}`,
      );

      setApplications(res.data.applications || []);
      setPagination(
        res.data.pagination || {
          total: 0,
          currentPage: 1,
          totalPages: 1,
          limit: itemsPerPage,
        },
      );
    } catch (error) {
      console.error("Error fetching applications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests(currentPage);
  }, [currentPage]);

  const openRequestModal = (request) => {
    setSelectedApplication(request);
    setIsModalOpen(true);
  };

  const updateApplicationStatus = async (id, status) => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/job-application/update/${id}`,
        { status },
      );

      if (res.data.success) {
        toast.success("Application status updated");
        fetchRequests(currentPage);

        if (selectedApplication?._id === id) {
          setSelectedApplication((prev) => ({
            ...prev,
            status,
          }));
        }
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  const deleteRequest = async (id) => {
    const isConfirmed = await confirmAndDelete({
      id,
      url: `${process.env.REACT_APP_API_URL}/job-application/delete`,
    });

    if (isConfirmed) {
      try {
        await fetchRequests(currentPage);
        const newTotalPages = Math.ceil(
          (applications.length - 1) / itemsPerPage,
        );
        if (currentPage > newTotalPages) {
          setCurrentPage(newTotalPages || 1);
        }

        Swal.fire({
          title: "Deleted!",
          text: "The application has been deleted.",
          icon: "success",
          confirmButtonColor: "#10B981",
        });
      } catch (err) {
        console.error("Error deleting application:", err);
        Swal.fire({
          title: "Error!",
          text: "Something went wrong.",
          icon: "error",
          confirmButtonColor: "#EF4444",
        });
      }
    }
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getVisiblePages = () => {
    if (totalPages <= 4) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 3) {
      return [1, 2, 3, "...", totalPages];
    }

    if (currentPage >= totalPages - 2) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", currentPage, "...", totalPages];
  };

  return (
    <div className="min-h-screen bg-transparent p-6">
      <div>
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-heading font-bold bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor bg-clip-text text-transparent">
            Job Applications
          </h1>
          <div className="bg-primaryBtnColor text-white px-4 py-2 rounded-lg font-semibold">
            Total: {pagination.total} applications
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {loading
            ? Array.from({ length: itemsPerPage }).map((_, i) => (
                <RequestCardSkeleton key={i} />
              ))
            : applications.map((request) => (
                <div
                  key={request._id}
                  className="bg-white rounded-2xl overflow-hidden shadow-xl border border-primaryTextColor transition-transform hover:scale-[1.02] relative"
                >
                  <div className="absolute top-4 right-4 flex space-x-2 z-10">
                    <button
                      onClick={() => openRequestModal(request)}
                      className="p-2 bg-white bg-opacity-90 rounded-lg text-primaryBtnHoverColor hover:bg-primaryBtnColor hover:text-white transition-colors shadow-md"
                      title="View Details"
                    >
                      <RiEyeLine size={16} />
                    </button>
                    <button
                      onClick={() => deleteRequest(request._id)}
                      className="p-2 bg-white bg-opacity-90 rounded-lg text-red-500 hover:bg-red-500 hover:text-white transition-colors shadow-md"
                      title="Delete Request"
                    >
                      <RiDeleteBinLine size={16} />
                    </button>
                  </div>

                  <div className="p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor rounded-full flex items-center justify-center text-white font-bold mr-4">
                        {request.applicantName?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-primaryTextColor">
                          {request.applicantName}
                        </h3>
                        <div className="flex items-center text-sm text-secondaryTextColor mb-1">
                          <RiMailLine size={14} className="mr-1" />
                          <span className="truncate">{request.email}</span>
                        </div>
                        <div className="flex items-center text-sm text-primaryBtnHoverColor">
                          <RiPhoneLine size={14} className="mr-1" />
                          <span>{request.phone}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="flex items-center text-sm font-medium text-primaryBtnColor mb-2">
                        <RiBriefcaseLine size={14} className="mr-2" />
                        <span>{request.jobPost?.roleName || "N/A"}</span>
                      </div>
                      <p className="text-secondaryTextColor min-h-12 line-clamp-2 text-sm leading-relaxed">
                        {request.applicantDetails ||
                          "No applicant details provided"}
                      </p>
                    </div>

                    <div className="flex items-end justify-between gap-3">
                      <div className="flex items-center text-xs text-gray-500">
                        <RiCalendarLine size={12} className="mr-1" />
                        <span>{formatDate(request.createdAt)}</span>
                      </div>

                      <select
                        value={request.status}
                        onChange={(e) =>
                          updateApplicationStatus(request._id, e.target.value)
                        }
                        className={`text-xs font-medium px-2 py-1 rounded-full border outline-none cursor-pointer ${
                          request.status === "Approved"
                            ? "bg-green-100 text-green-600 border-green-200"
                            : request.status === "Rejected"
                              ? "bg-red-100 text-red-600 border-red-200"
                              : "bg-yellow-100 text-yellow-600 border-yellow-200"
                        }`}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Approved">Approved</option>
                        <option value="Rejected">Rejected</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
        </div>

        {!loading && applications.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <RiBriefcaseLine size={32} className="text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              No applications yet
            </h3>
            <p className="text-gray-500">
              Job applications will appear here once candidates apply.
            </p>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-8 flex-wrap">
            {/* First */}
            <button
              onClick={() => paginate(1)}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                currentPage === 1
                  ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
                  : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
              }`}
            >
              First
            </button>

            {/* Previous */}
            <button
              onClick={() => currentPage > 1 && paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className={`p-2 rounded-lg ${
                currentPage === 1
                  ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
                  : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
              }`}
            >
              <RiArrowLeftSLine size={20} />
            </button>

            {/* Page Numbers */}
            {getVisiblePages().map((page, index) =>
              page === "..." ? (
                <span
                  key={`ellipsis-${index}`}
                  className="px-2 py-1 text-gray-400 font-medium"
                >
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => paginate(page)}
                  className={`px-3 py-1 rounded-lg text-sm ${
                    currentPage === page
                      ? "bg-primaryBtnColor text-white"
                      : "bg-primaryTextColor text-gray-300 hover:bg-secondaryTextColor"
                  }`}
                >
                  {page}
                </button>
              ),
            )}

            {/* Next */}
            <button
              onClick={() =>
                currentPage < totalPages && paginate(currentPage + 1)
              }
              disabled={currentPage === totalPages}
              className={`p-2 rounded-lg ${
                currentPage === totalPages
                  ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
                  : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
              }`}
            >
              <RiArrowRightSLine size={20} />
            </button>

            {/* Last */}
            <button
              onClick={() => paginate(totalPages)}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                currentPage === totalPages
                  ? "bg-primaryTextColor text-gray-500 cursor-not-allowed"
                  : "bg-primaryBtnColor text-white hover:bg-primaryBtnHoverColor"
              }`}
            >
              Last
            </button>
          </div>
        )}

        {isModalOpen && selectedApplication && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto border border-gray-300 shadow-xl">
              <div className="sticky top-0 bg-white p-4 border-b border-gray-300 flex justify-between items-center">
                <h2 className="text-xl font-bold text-primaryTextColor">
                  Application Details
                </h2>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="text-gray-500 hover:text-primaryBtnHoverColor"
                >
                  <RiCloseLine size={24} />
                </button>
              </div>

              <div className="p-6">
                <div className="bg-gradient-to-r from-primaryBtnColor to-primaryBtnHoverColor rounded-2xl p-6 mb-6 text-white">
                  <div className="flex items-center mb-4">
                    <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-white font-bold mr-4 text-xl">
                      {selectedApplication.applicantName
                        ?.charAt(0)
                        ?.toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">
                        {selectedApplication.applicantName}
                      </h3>
                      <p className="text-sm opacity-90">
                        {selectedApplication.jobPost?.roleName || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <RiMailLine
                          className="text-primaryBtnColor mr-2"
                          size={18}
                        />
                        <span className="font-semibold text-gray-700">
                          Email Address
                        </span>
                      </div>
                      <p className="text-primaryTextColor">
                        {selectedApplication.email}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <RiPhoneLine
                          className="text-primaryBtnColor mr-2"
                          size={18}
                        />
                        <span className="font-semibold text-gray-700">
                          Phone Number
                        </span>
                      </div>
                      <p className="text-primaryTextColor">
                        {selectedApplication.phone}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <RiBriefcaseLine
                          className="text-primaryBtnColor mr-2"
                          size={18}
                        />
                        <span className="font-semibold text-gray-700">
                          Applied For
                        </span>
                      </div>
                      <p className="text-primaryTextColor">
                        {selectedApplication.jobPost?.roleName || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <RiUserLine
                          className="text-primaryBtnColor mr-2"
                          size={18}
                        />
                        <span className="font-semibold text-gray-700">
                          Applicant Name
                        </span>
                      </div>
                      <p className="text-primaryTextColor">
                        {selectedApplication.applicantName}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <RiCalendarLine
                          className="text-primaryBtnColor mr-2"
                          size={18}
                        />
                        <span className="font-semibold text-gray-700">
                          Submitted On
                        </span>
                      </div>
                      <p className="text-primaryTextColor">
                        {formatDate(selectedApplication.createdAt)}
                      </p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center mb-2">
                        <RiFileTextLine
                          className="text-primaryBtnColor mr-2"
                          size={18}
                        />
                        <span className="font-semibold text-gray-700">
                          Resume
                        </span>
                      </div>
                      {selectedApplication.resume ? (
                        <a
                          href={selectedApplication.resume}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primaryBtnColor underline"
                        >
                          View Resume
                        </a>
                      ) : (
                        <p className="text-primaryTextColor">
                          No resume uploaded
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h4 className="font-semibold text-gray-700 mb-3">
                    Applicant Details
                  </h4>
                  <div className="bg-white p-4 rounded-lg border-l-4 border-primaryBtnColor">
                    <p className="text-primaryTextColor leading-relaxed">
                      {selectedApplication.applicantDetails ||
                        "No details provided"}
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    <RiBriefcaseLine
                      className="text-primaryBtnColor mr-2"
                      size={18}
                    />
                    <span className="font-semibold text-gray-700">
                      Application Status
                    </span>
                  </div>

                  <select
                    value={selectedApplication.status}
                    onChange={(e) =>
                      updateApplicationStatus(
                        selectedApplication._id,
                        e.target.value,
                      )
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-primaryTextColor focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Approved">Approved</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="bg-gray-100 text-primaryTextColor py-2 px-6 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      deleteRequest(selectedApplication._id);
                      setIsModalOpen(false);
                    }}
                    className="bg-red-500 text-white py-2 px-6 rounded-lg hover:bg-red-600 transition-colors flex items-center"
                  >
                    <RiDeleteBinLine className="mr-2" size={16} />
                    Delete Application
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobApplications;
